import {z} from 'zod';

// The settings endpoint types its `value` as `any`, and a partially migrated tenant can
// still hold the pre-per-locale singular `emailContextTemplate` key, so the shape is
// parsed rather than asserted. Mirrors `AutomaticCustomerMessageSettings` and its check-in subtype.

export const bodyFormatSchema = z.enum(['text', 'html']).catch('text');

export const emailContextTemplateValueSchema = z.object({
  locale: z.string(),
  subject: z.string(),
  body: z.string(),
  bodyFormat: bodyFormatSchema.optional(),
});

export const smsContextTemplateValueSchema = z.object({
  locale: z.string(),
  body: z.string(),
});

/**
 * Validates entry by entry and keeps the ones that pass. Rejecting the whole array would
 * load the form blank, and the next save writes that emptied array back over every
 * locale — the silent data loss this screen exists to prevent.
 */
const arrayOfValidEntries = <TEntry extends z.ZodTypeAny>(entrySchema: TEntry) =>
  z
    .array(z.unknown())
    .catch([])
    .transform((entries) =>
      entries.flatMap((entry): z.infer<TEntry>[] => {
        const result = entrySchema.safeParse(entry);

        return result.success ? [result.data] : [];
      })
    );

export const automaticMessageSettingsValueSchema = z.object({
  enabled: z.boolean().optional(),
  smsEnabled: z.boolean().optional(),
  emailEnabled: z.boolean().optional(),
  emailCredentialsId: z.string().nullish(),
  sendAfterDays: z.number().int().optional().catch(undefined),
  sendAtTime: z.string().optional().catch(undefined),
  emailContextTemplates: arrayOfValidEntries(emailContextTemplateValueSchema).optional(),
  smsContextTemplates: arrayOfValidEntries(smsContextTemplateValueSchema).optional(),
});

export type EmailContextTemplateValue = z.infer<typeof emailContextTemplateValueSchema>;
export type SmsContextTemplateValue = z.infer<typeof smsContextTemplateValueSchema>;
export type AutomaticMessageSettingsValue = z.infer<typeof automaticMessageSettingsValueSchema>;

export const parseAutomaticMessageSettingsValue = (value: unknown): AutomaticMessageSettingsValue =>
  automaticMessageSettingsValueSchema.safeParse(value ?? {}).data ?? {};
